
import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from 'app/services/api.service';
import { RemoveConnectedDeviceDialogComponent } from './remove-connected-device/remove-connected-device.component';
import { MatDialog } from '@angular/material/dialog';
import { ConnectDevice } from 'app/models/connect-device';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';

interface ActivationOption {
    id: string;
    name: string;
    group: number;
    node: number;
}

@Component({
    selector: 'device-connect',
    templateUrl: './device-connect.component.html',
    styleUrls: ['./device-connect.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [FormsModule, TranslateModule, MatIconModule, MatSelectModule, MatFormFieldModule]
})
export class DeviceConnectComponent implements OnInit {

  @Input() device: any;
  @Input() tagId: string;
  deviceMap = new Map<string, any>();
  connectedDevices: any[] = [];
  allDevices: any[] = [];
  selectedDeviceId: string = '';

  editMode: boolean = false;
  connectRowVisible: boolean = false;
  connectRowClosing: boolean = false;
  loading: boolean = false;

  deviceList: any[] = [];
  filteredDeviceList: any[] = [];
  deviceSearchText: string = '';
  selectedDeviceArray: any[] = [];
  selectedActivationArray: any[] = [];
  activationList: ActivationOption[] = [];

  constructor(private api: ApiService, private dialog: MatDialog, private translate: TranslateService) {}

  toggleEditMode(): void {
    if (this.editMode) {
      this.editMode = false;
      this.connectRowClosing = true;
      setTimeout(() => {
        this.connectRowVisible = false;
        this.connectRowClosing = false;
      }, 250);
    } else {
      this.editMode = true;
      this.connectRowVisible = true;
    }
  }

  ngOnInit(): void {
   this.loading = true;
    this.loadAllDevices();
    this.loadActivations();
  }

  filterDeviceList(): void {
    const search = this.deviceSearchText.trim().toLowerCase();
    this.filteredDeviceList = !search
      ? this.deviceList
      : this.deviceList.filter(dev => dev.name.toLowerCase().includes(search));
  }

  onDeviceSelectOpenedChange(opened: boolean): void {
    if (!opened) {
      this.deviceSearchText = '';
      this.filterDeviceList();
    }
  }


  loadConnectedDevices(): void {
    if (!this.device?.id) return;

    this.api.getConnectedDevices(this.device.id).subscribe({
      next: (res: any[]) => {

        const actAll = JSON.parse(localStorage.getItem('act')) || [];

        const devicesMap = new Map<string, any>();

        res.forEach(dev => {
          const fullDev = this.deviceMap.get(dev.id) || {};
          const devActs = dev.activations?.map(act => {
            const match = actAll.find(a =>
              a.group === act.group &&
              a.node === act.node &&
              a.deviceType === fullDev.deviceType
            );
            return match?.name || '-';
          }) || [];

          if (devicesMap.has(dev.id)) {
            const existing = devicesMap.get(dev.id);
            existing.activationNames = Array.from(new Set([...existing.activationNames, ...devActs]));
          } else {
            devicesMap.set(dev.id, {
              ...fullDev,
              id: dev.id,
              activationNames: devActs
            });
          }
        });
        this.connectedDevices = Array.from(devicesMap.values()).map(d => ({
          ...d,
          activationName: d.activationNames.join(', ')
        }));

        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  loadAllDevices(): void {
    this.api.getDevices({ fullInfo: false }).subscribe((res: any[]) => {
      const filtered = res.filter(dev => dev.deviceType !== "33-255");
      this.allDevices = filtered;

      this.deviceMap.clear();
      filtered.forEach(dev => this.deviceMap.set(dev.id, dev));
      this.deviceList = filtered.map(dev => ({
        id: dev.id,
        name: dev.name ? `${dev.name} (${dev.id})` : dev.id,
        device: dev
      }));
      this.filteredDeviceList = this.deviceList;
      this.loadConnectedDevices();
    });
  }

  connectDevice(): void {
    if (!this.selectedDeviceArray.length || !this.selectedActivationArray.length) return;

    const selectedDev = this.selectedDeviceArray[0];
  
    const selectedActs = this.selectedActivationArray
      .map(actId => this.activationList.find(a => a.id === actId))
      .filter(a => !!a);
  
    const payload: ConnectDevice = {
      deviceId: selectedDev.id,
      activations: selectedActs.map(a => ({
        group: a.group,
        node: a.node
      }))
    };
  
    this.api.addConnectedDevice(this.device.id, payload).subscribe({
      next: () => {
        this.loadConnectedDevices();
      },
      error: err => console.error('Failed to connect device', err)
    });

    this.selectedDeviceArray = [];
    this.deviceSearchText = '';
    this.filterDeviceList();
    this.selectedActivationArray = [];
  }

  loadActivations(): void {
    const act = JSON.parse(localStorage.getItem('act')) || [];
    this.activationList = act.map((a, index) => ({
      ...a,
      id: a.id || `${a.deviceType}-${a.group}-${a.node}-${index}`
    }));
  }

    onDeviceChange(selectedDev: any): void {
        this.selectedDeviceArray = selectedDev ? [selectedDev] : [];

        if (!selectedDev) {
            this.selectedActivationArray = [];
            this.activationList = [];
            return;
        }

        const actAll = JSON.parse(localStorage.getItem('act')) || [];
        this.activationList = actAll
            .filter(a => a.deviceType === selectedDev.device.deviceType)
            .map((a, index) => ({
                ...a,
                id: a.id || `${a.deviceType}-${a.group}-${a.node}-${index}`
            }));

        this.selectedActivationArray = [];
    }

  removeDevice(deviceId: string, deviceName?: string): void {
    const dialogRef = this.dialog.open(RemoveConnectedDeviceDialogComponent, {
      width: '400px',
      data: { 
        deviceName,
        deviceId
      }
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.api.removeConnectedDevice(this.device.id, deviceId).subscribe({
          next: () => this.loadConnectedDevices(),
          error: (err) => console.error('Failed to remove device', err)
        });
      }
    });
  }

  getDeviceType(type: string): string {
    return this.api.getDeviceType(type);
  }
}